import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  X,
  Zap,
  Cpu,
  ShieldCheck,
  Cctv,
  Gauge,
  Antenna,
  CheckCircle2,
  ArrowUpRight,
  Calendar,
  Layers,
  Menu,
  Star,
  Quote,
} from "lucide-react";
import { FEATURED, VOLT } from "@/lib/elektryk-gallery";
import { elektrykPublicListReviews } from "@/lib/elektryk-admin.functions";

export const Route = createFileRoute("/demo/elektryk")({
  head: () => ({
    meta: [
      { title: "ELEKTROPROFI — Instalacje elektryczne, Bytom" },
      {
        name: "description",
        content:
          "ELEKTROPROFI. 20 lat praktyki. Instalacje, automatyka, rozdzielnice n/n. Bytom, Tarnowskie Góry, Śląsk.",
      },
      { property: "og:title", content: "ELEKTROPROFI — Instalacje bez kompromisów" },
      {
        property: "og:description",
        content: "Systemy zasilania, automatyka budynkowa, prefabrykacja rozdzielnic.",
      },
    ],
    links: [
      { rel: "preconnect", href: "https://images.unsplash.com", crossOrigin: "anonymous" },
    ],
  }),
  component: ElektroProfiDemo,
});

const NAV = [
  { id: "start", label: "Start" },
  { id: "o-nas", label: "O nas" },
  { id: "uprawnienia", label: "Uprawnienia" },
  { id: "uslugi", label: "Usługi" },
  { id: "realizacje", label: "Realizacje" },
  { id: "opinie", label: "Opinie" },
  { id: "kontakt", label: "Kontakt" },
];

const REALIZATIONS = [
  { client: "GM Opel Gliwice", scope: "Linia robotów spawalniczych, modernizacja komór lakierniczych." },
  { client: "Wiromet S.A. Mikołów", scope: "Adaptacja hal, instalacje oświetleniowe, rozdzielnie n/n." },
  { client: "C.H. Plaza Ruda Śląska", scope: "Kompletne systemy zasilania i instalacje elektryczne." },
  { client: "Bytom — wspólnoty mieszk.", scope: "Gruntowne remonty instalacji budynków miejskich." },
];

const SERVICES = [
  { no: "01", icon: Zap, title: "Instalacje elektryczne", desc: "Od przyłącza po gniazdko. Domy, przemysł, modernizacje." },
  { no: "02", icon: Cpu, title: "Smart Home & automatyka", desc: "Fibaro, F&Home, xComfort. Sterowanie z telefonu." },
  { no: "03", icon: Layers, title: "Prefabrykacja rozdzielnic", desc: "Szafy n/n do 2500A. Eaton, Hager, Legrand, Rittal." },
  { no: "04", icon: Cctv, title: "Bezpieczeństwo & CCTV", desc: "Satel, kontrola dostępu, monitoring HQVision." },
  { no: "05", icon: Gauge, title: "Pomiary i audyty", desc: "Pomiary 5-letnie, oświetlenie, nadzór inwestycji." },
  { no: "06", icon: Antenna, title: "Teletechnika & odgromy", desc: "RTV/SAT, multiswitche, ochrona odgromowa." },
];

const SEP_ITEMS = [
  "Urządzenia, instalacje i sieci do 20 kV",
  "Zespoły prądotwórcze do 150 kW",
  "Sieci oświetlenia ulicznego",
  "Urządzenia w wykonaniu przeciwwybuchowym do 1 kV",
  "Pomiary ochrony p.porażeniowej i odgromowej",
];

const PARTNER_CERTS = [
  { brand: "FIBARO", title: "Certyfikowany instalator automatyki budynku" },
  { brand: "EATON", title: "Autoryzowany instalator xComfort i rozdzielnic do 2500A" },
  { brand: "HQVISION", title: "Autoryzowany instalator monitoringu CCTV" },
  { brand: "TIM", title: "Uziemienia i pomiary rezystancji" },
];

function ElektroProfiDemo() {
  return (
    <div className="min-h-screen overflow-x-clip bg-[#070708] font-sans text-[#F5F5F7] antialiased selection:bg-[#00F0FF] selection:text-black">
      <BackToDemo />
      <Nav />
      <main className="overflow-x-clip">
        <Stage>
          <Hero />
          <Brand />
        </Stage>
        <Qualifications />
        <Services />
        <Gallery />
        <Reviews />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

function BackToDemo() {
  return (
    <div className="fixed left-4 top-4 z-[70] md:left-6 md:top-6">
      <Link
        to="/demo"
        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/80 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/70 transition-colors hover:border-[#00F0FF]/40 hover:text-white"
      >
        <ArrowLeft size={12} />
        Demo
      </Link>
    </div>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("start");

  useEffect(() => {
    const sections = NAV.map((n) => document.getElementById(n.id)).filter(Boolean) as HTMLElement[];
    if (sections.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] },
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-[60] flex justify-center px-4 pt-4 md:pt-5">
      <div className="flex w-full max-w-6xl items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-[#0B0B0E]/95 px-3 py-2 md:rounded-full md:px-5 md:py-2.5">
        <a href="#start" className="flex items-center pl-12 text-sm font-bold tracking-[0.22em] text-white md:pl-2 md:text-[15px]">
          ELEKTROPROFI
          <span
            className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 10px ${VOLT}` }}
          />
        </a>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`relative rounded-full px-3 py-1.5 text-xs tracking-wide transition ${
                active === item.id ? "text-white" : "text-white/75 hover:text-white"
              }`}
            >
              {active === item.id && (
                <span
                  className="absolute inset-0 -z-10 rounded-full"
                  style={{ backgroundColor: `${VOLT}15`, boxShadow: `inset 0 0 0 1px ${VOLT}40` }}
                />
              )}
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:+48608096616"
            className="hidden items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-black sm:inline-flex md:px-4 md:py-2"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 22px ${VOLT}55` }}
          >
            <Phone size={12} />
            608 096 616
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-white/70 lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="absolute left-4 right-4 top-[68px] z-[59] rounded-2xl border border-white/10 bg-[#0B0B0E]/98 p-3 lg:hidden animate-fade-in">
            <div className="grid gap-1">
              {NAV.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setOpen(false)}
                  className={`rounded-xl px-4 py-3 text-sm tracking-wide transition ${
                    active === item.id ? "bg-white/[0.06] text-white" : "text-white/70 hover:bg-white/[0.04] hover:text-white"
                  }`}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="tel:+48608096616"
                onClick={() => setOpen(false)}
                className="mt-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-black"
                style={{ backgroundColor: VOLT }}
              >
                <Phone size={14} /> 608 096 616
              </a>
            </div>
        </div>
      )}
    </header>
  );
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, min = 0, max = 1) => Math.max(min, Math.min(max, v));

function Stage({ children }: { children: React.ReactNode }) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const ghostBoltRef = useRef<SVGSVGElement | null>(null);
  const beamRef = useRef<HTMLDivElement | null>(null);
  const meterFillRef = useRef<HTMLDivElement | null>(null);
  const meterTickRef = useRef<HTMLDivElement | null>(null);
  const depthFarRef = useRef<HTMLDivElement | null>(null);
  const depthMidRef = useRef<HTMLDivElement | null>(null);
  const auroraRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const vh = window.innerHeight;
      const span = Math.max(1, rect.height - vh);
      const p = clamp(-rect.top / span);

      // Ghost bolt: travels from upper-right of hero → behind "20+" in brand,
      // growing and rotating along a curved arc.
      if (ghostBoltRef.current) {
        const arc = Math.sin(p * Math.PI) * 8; // curve in the middle
        const x = lerp(38, -22, p) + arc;
        const y = lerp(-6, 28, p);
        const rot = lerp(10, -18, p);
        const scale = lerp(0.95, 1.9, p);
        let op = 1;
        if (p < 0.04) op = p / 0.04;
        else if (p > 0.78) op = clamp(1 - (p - 0.78) / 0.22);
        ghostBoltRef.current.style.transform = `translate3d(${x}vw, ${y}vh, 0) rotate(${rot}deg) scale(${scale})`;
        ghostBoltRef.current.style.opacity = String(op * 0.55);
      }

      // Horizontal energy beam fires at the hero→brand transition.
      if (beamRef.current) {
        const b = Math.max(0, 1 - Math.abs(p - 0.46) * 7);
        beamRef.current.style.transform = `scaleX(${b})`;
        beamRef.current.style.opacity = String(b);
      }

      // Voltage meter pinned to right edge while in stage.
      if (meterFillRef.current) {
        const fill = clamp(p * 1.4);
        meterFillRef.current.style.transform = `scaleY(${fill})`;
        meterFillRef.current.style.opacity = String(clamp(1 - (p - 0.6) * 3));
      }
      if (meterTickRef.current) {
        meterTickRef.current.style.transform = `translateY(${-clamp(p * 1.4) * 100}%)`;
        meterTickRef.current.style.opacity = String(clamp(1 - (p - 0.6) * 3));
      }

      // Background depth layers — different speeds = parallax.
      if (depthFarRef.current) {
        depthFarRef.current.style.transform = `translate3d(0, ${-p * vh * 0.25}px, 0)`;
      }
      if (depthMidRef.current) {
        depthMidRef.current.style.transform = `translate3d(0, ${-p * vh * 0.55}px, 0)`;
      }
      if (auroraRef.current) {
        auroraRef.current.style.transform = `translate3d(${(p - 0.5) * 30}vw, ${-p * vh * 0.4}px, 0)`;
        auroraRef.current.style.opacity = String(clamp(0.45 - Math.abs(p - 0.5) * 0.6));
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div ref={stageRef} className="relative overflow-x-clip">
      {/* Far depth layer — drifts slowest */}
      <div className="pointer-events-none sticky top-0 z-0 h-0">
        <div
          ref={depthFarRef}
          aria-hidden
          className="absolute inset-x-0 top-0 h-screen"
          style={{
            background: `radial-gradient(60vw 60vh at 80% 10%, ${VOLT}10, transparent 60%), radial-gradient(50vw 50vh at 10% 90%, ${VOLT}08, transparent 60%)`,
            willChange: "transform",
          }}
        />
      </div>

      {/* Mid depth aurora ribbon */}
      <div className="pointer-events-none sticky top-0 z-0 h-0">
        <div
          ref={auroraRef}
          aria-hidden
          className="absolute left-1/2 top-1/2 h-[60vmin] w-[140vw] -translate-x-1/2 -translate-y-1/2 rotate-[-8deg]"
          style={{
            background: `linear-gradient(90deg, transparent, ${VOLT}1F 45%, ${VOLT}26 50%, ${VOLT}1F 55%, transparent)`,
            filter: "blur(0px)",
            maskImage: "linear-gradient(180deg, transparent, black 40%, black 60%, transparent)",
            willChange: "transform, opacity",
          }}
        />
      </div>

      {/* Mid depth secondary glow */}
      <div className="pointer-events-none sticky top-0 z-0 h-0">
        <div
          ref={depthMidRef}
          aria-hidden
          className="absolute inset-x-0 top-0 h-screen"
          style={{
            backgroundImage:
              `linear-gradient(${VOLT}06 1px, transparent 1px), linear-gradient(90deg, ${VOLT}06 1px, transparent 1px)`,
            backgroundSize: "140px 140px",
            maskImage:
              "radial-gradient(ellipse at 70% 30%, black 0%, transparent 70%)",
            willChange: "transform",
          }}
        />
      </div>

      {/* Voltage meter — pinned to viewport edge while traversing stage */}
      <div className="pointer-events-none sticky top-0 z-[6] h-0">
        <div className="absolute right-2 top-1/2 -translate-y-1/2 md:right-4">
          <div className="relative h-44 w-1 overflow-hidden rounded-full bg-white/[0.06]">
            <div
              ref={meterFillRef}
              className="absolute inset-x-0 bottom-0 h-full origin-bottom"
              style={{
                background: `linear-gradient(to top, ${VOLT}, ${VOLT}88)`,
                boxShadow: `0 0 14px ${VOLT}`,
                transform: "scaleY(0)",
                willChange: "transform, opacity",
              }}
            />
          </div>
          <div
            ref={meterTickRef}
            className="absolute -left-1 bottom-0 h-px w-3"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 8px ${VOLT}`, willChange: "transform, opacity" }}
          />
        </div>
      </div>

      {/* Ghost lightning bolt — crosses both sections */}
      <div className="pointer-events-none sticky top-0 z-0 h-0">
        <svg
          ref={ghostBoltRef}
          aria-hidden
          viewBox="0 0 400 600"
          className="absolute left-1/2 top-1/2 h-[60vh] w-auto -translate-x-1/2 -translate-y-1/2"
          style={{
            willChange: "transform, opacity",
            filter: `drop-shadow(0 0 36px ${VOLT}55)`,
            transformOrigin: "center center",
          }}
        >
          <path
            d="M230 20 L120 280 L210 280 L150 580 L300 240 L210 240 Z"
            fill={`${VOLT}10`}
            stroke={VOLT}
            strokeOpacity="0.55"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Horizontal energy beam at section transition */}
      <div className="pointer-events-none sticky top-0 z-[5] h-0">
        <div
          ref={beamRef}
          aria-hidden
          className="absolute left-0 top-[58vh] h-px w-full origin-left"
          style={{
            background: `linear-gradient(90deg, transparent, ${VOLT}, transparent)`,
            boxShadow: `0 0 30px ${VOLT}, 0 0 80px ${VOLT}55`,
            transform: "scaleX(0)",
            opacity: 0,
            willChange: "transform, opacity",
          }}
        />
      </div>

      {children}
    </div>
  );
}

function Hero() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const glowRef = useRef<HTMLDivElement | null>(null);
  const orb1Ref = useRef<HTMLDivElement | null>(null);
  const orb2Ref = useRef<HTMLDivElement | null>(null);
  const circuitRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const scanRef = useRef<HTMLDivElement | null>(null);
  const boltRef = useRef<SVGSVGElement | null>(null);
  const vignetteRef = useRef<HTMLDivElement | null>(null);
  const haloRef = useRef<HTMLDivElement | null>(null);
  const horizonRef = useRef<HTMLDivElement | null>(null);
  const line1Ref = useRef<HTMLSpanElement | null>(null);
  const line2Ref = useRef<HTMLSpanElement | null>(null);
  const badgeRef = useRef<HTMLDivElement | null>(null);
  const sparksRef = useRef<HTMLDivElement | null>(null);
  const [tiltReady, setTiltReady] = useState(false);
  const [needsTiltPermission, setNeedsTiltPermission] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    let scrollY = 0;
    let lastScrollY = 0;
    let velocity = 0;
    let flash = 0;
    let mx = 0;
    let my = 0;
    let tx = 0;
    let ty = 0;
    let ticking = false;
    let rafActive = false;

    const apply = () => {
      ticking = false;
      // smooth pointer
      tx += (mx - tx) * 0.08;
      ty += (my - ty) * 0.08;

      // velocity decay
      velocity *= 0.85;
      flash *= 0.9;

      const y = scrollY;
      const skew = Math.max(-2, Math.min(2, velocity * 0.04));

      if (gridRef.current) {
        gridRef.current.style.transform = `translate3d(${tx * -10}px, ${y * 0.18 + ty * -8}px, 0) skewY(${skew}deg)`;
      }
      if (glowRef.current) {
        glowRef.current.style.transform = `translate3d(${tx * 14}px, ${y * 0.32 + ty * 12}px, 0)`;
        glowRef.current.style.opacity = String(Math.max(0, 1 - y / 900));
      }
      if (circuitRef.current) {
        circuitRef.current.style.transform = `translate3d(${tx * -6}px, ${y * 0.45}px, 0)`;
        circuitRef.current.style.opacity = String(Math.max(0, 0.35 - y / 1400));
      }
      if (orb1Ref.current) {
        orb1Ref.current.style.transform = `translate3d(${tx * 34}px, ${y * 0.55 + ty * 26}px, 0)`;
      }
      if (orb2Ref.current) {
        orb2Ref.current.style.transform = `translate3d(${tx * -26}px, ${y * 0.7 + ty * -22}px, 0)`;
      }
      if (haloRef.current) {
        haloRef.current.style.transform = `translate3d(${tx * 18}px, ${y * 0.25 + ty * 14}px, 0) scale(${1 + flash * 0.0008})`;
        haloRef.current.style.opacity = String(Math.max(0, 0.9 - y / 700) + flash * 0.0015);
      }
      if (scanRef.current) {
        scanRef.current.style.transform = `translate3d(0, ${y * 0.9}px, 0)`;
        scanRef.current.style.opacity = String(0.6 + Math.min(1, flash * 0.004));
      }
      if (boltRef.current) {
        boltRef.current.style.transform = `translate3d(${tx * -16}px, ${y * 0.38 + ty * -12}px, 0) rotate(${tx * 1.2}deg)`;
        boltRef.current.style.opacity = String(Math.max(0, 1 - y / 800));
      }
      if (vignetteRef.current) {
        vignetteRef.current.style.opacity = String(Math.min(1, 0.4 + y / 1200));
      }
      if (horizonRef.current) {
        horizonRef.current.style.transform = `translate3d(0, ${y * 0.05}px, 0) skewY(${skew * -0.5}deg)`;
      }
      if (sparksRef.current) {
        sparksRef.current.style.transform = `translate3d(${tx * 8}px, ${y * 0.62 + ty * 8}px, 0)`;
      }
      if (badgeRef.current) {
        badgeRef.current.style.transform = `translate3d(${tx * 6}px, ${ty * 4}px, 0)`;
      }
      if (line1Ref.current) {
        line1Ref.current.style.transform = `translate3d(${tx * -4}px, ${y * -0.05 + ty * -3}px, 0)`;
      }
      if (line2Ref.current) {
        line2Ref.current.style.transform = `translate3d(${tx * 4}px, ${y * -0.02 + ty * 3}px, 0)`;
      }
      if (contentRef.current) {
        const p = Math.min(1, y / 600);
        contentRef.current.style.transform = `translate3d(0, ${y * -0.1}px, 0)`;
        contentRef.current.style.opacity = String(1 - p * 0.85);
      }

      // keep rAF alive while pointer easing or flash/velocity decaying
      if (
        Math.abs(tx - mx) > 0.05 ||
        Math.abs(ty - my) > 0.05 ||
        Math.abs(velocity) > 0.2 ||
        flash > 0.5
      ) {
        ticking = true;
        requestAnimationFrame(apply);
      } else {
        rafActive = false;
      }
    };

    const kick = () => {
      if (!ticking) {
        ticking = true;
        rafActive = true;
        requestAnimationFrame(apply);
      }
    };

    const onScroll = () => {
      scrollY = window.scrollY;
      const dy = scrollY - lastScrollY;
      velocity = dy;
      if (Math.abs(dy) > 14) flash = Math.min(300, flash + Math.abs(dy) * 2);
      lastScrollY = scrollY;
      kick();
    };
    const onPointer = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      mx = (e.clientX / w - 0.5) * 2;
      my = (e.clientY / h - 0.5) * 2;
      kick();
    };

    // Gyroscope parallax on touch devices
    let tiltCleanup: (() => void) | null = null;
    const enableTilt = () => {
      const onOrient = (e: DeviceOrientationEvent) => {
        if (e.beta == null || e.gamma == null) return;
        // gamma: left/right [-90,90], beta: front/back [-180,180]
        mx = Math.max(-1, Math.min(1, e.gamma / 35));
        my = Math.max(-1, Math.min(1, (e.beta - 45) / 45));
        kick();
      };
      window.addEventListener("deviceorientation", onOrient);
      tiltCleanup = () => window.removeEventListener("deviceorientation", onOrient);
      setTiltReady(true);
    };
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) {
      const DOE = (window as any).DeviceOrientationEvent;
      if (DOE && typeof DOE.requestPermission === "function") {
        setNeedsTiltPermission(true);
      } else if (DOE) {
        enableTilt();
      }
    }
    (window as any).__elektrykEnableTilt = async () => {
      const DOE = (window as any).DeviceOrientationEvent;
      try {
        const res = await DOE.requestPermission();
        if (res === "granted") {
          enableTilt();
          setNeedsTiltPermission(false);
        }
      } catch {}
    };

    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointermove", onPointer, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onPointer);
      if (tiltCleanup) tiltCleanup();
      delete (window as any).__elektrykEnableTilt;
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="start"
      className="relative flex min-h-[100svh] items-center overflow-hidden px-5 pt-28 md:px-10 md:pt-32"
      style={{ perspective: "1200px" }}
    >
      <style>{`
        @keyframes elektrykBoltDraw {
          0% { stroke-dashoffset: 320; opacity: 0; }
          15% { opacity: 1; }
          50% { stroke-dashoffset: 0; opacity: 1; }
          70% { opacity: 0.2; }
          100% { stroke-dashoffset: -320; opacity: 0; }
        }
        @keyframes elektrykSparkFloat {
          0% { transform: translate3d(0,0,0); opacity: 0; }
          20% { opacity: 1; }
          100% { transform: translate3d(var(--dx,0), -120px, 0); opacity: 0; }
        }
      `}</style>

      <div
        ref={gridRef}
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse at 30% 40%, black 30%, transparent 75%)",
          willChange: "transform",
          transformOrigin: "center center",
        }}
      />

      {/* deep halo behind title */}
      <div
        ref={haloRef}
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[80vmin] w-[80vmin] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          background: `radial-gradient(circle, ${VOLT}22, transparent 60%)`,
          willChange: "transform, opacity",
        }}
      />

      <div
        ref={glowRef}
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            `radial-gradient(560px 420px at 12% 35%, ${VOLT}1F, transparent 70%), radial-gradient(440px 360px at 95% 95%, ${VOLT}10, transparent 70%)`,
          willChange: "transform, opacity",
        }}
      />
      <div
        ref={circuitRef}
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            `linear-gradient(90deg, ${VOLT}10 1px, transparent 1px), linear-gradient(${VOLT}08 1px, transparent 1px)`,
          backgroundSize: "240px 240px, 240px 240px",
          maskImage:
            "linear-gradient(180deg, transparent 0%, black 30%, black 70%, transparent 100%)",
          willChange: "transform, opacity",
        }}
      />

      {/* animated lightning bolt SVG */}
      <svg
        ref={boltRef}
        aria-hidden
        viewBox="0 0 400 600"
        className="pointer-events-none absolute right-[-10%] top-[8%] h-[70%] w-[80%] sm:right-[5%] sm:w-[55%] md:w-[40%]"
        style={{ willChange: "transform, opacity", filter: `drop-shadow(0 0 18px ${VOLT}66)` }}
      >
        <path
          d="M230 20 L120 280 L210 280 L150 580 L300 240 L210 240 Z"
          fill="none"
          stroke={VOLT}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeDasharray="320"
          style={{ animation: "elektrykBoltDraw 4.5s ease-in-out infinite" }}
        />
        <path
          d="M230 20 L120 280 L210 280 L150 580 L300 240 L210 240 Z"
          fill={`${VOLT}08`}
          stroke={`${VOLT}40`}
          strokeWidth="1"
        />
      </svg>

      <div
        ref={orb1Ref}
        aria-hidden
        className="pointer-events-none absolute -left-20 top-1/4 h-72 w-72 rounded-full"
        style={{
          background: `radial-gradient(circle, ${VOLT}26, transparent 65%)`,
          willChange: "transform",
        }}
      />
      <div
        ref={orb2Ref}
        aria-hidden
        className="pointer-events-none absolute -right-24 bottom-10 h-96 w-96 rounded-full"
        style={{
          background: `radial-gradient(circle, ${VOLT}1C, transparent 65%)`,
          willChange: "transform",
        }}
      />

      {/* floating sparks */}
      <div
        ref={sparksRef}
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ willChange: "transform" }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <span
            key={i}
            className="absolute block h-1 w-1 rounded-full"
            style={{
              backgroundColor: VOLT,
              boxShadow: `0 0 8px ${VOLT}`,
              left: `${(i * 13 + 8) % 95}%`,
              top: `${60 + ((i * 37) % 35)}%`,
              opacity: 0,
              ["--dx" as any]: `${(i % 2 ? 1 : -1) * (10 + i * 4)}px`,
              animation: `elektrykSparkFloat ${4 + (i % 4)}s ease-out ${i * 0.5}s infinite`,
            }}
          />
        ))}
      </div>

      <div
        ref={scanRef}
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${VOLT}, transparent)`,
          boxShadow: `0 0 24px ${VOLT}`,
          willChange: "transform",
        }}
      />

      {/* horizon line at bottom */}
      <div
        ref={horizonRef}
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-32"
        style={{
          background: `linear-gradient(180deg, transparent, ${VOLT}10 60%, ${VOLT}22)`,
          willChange: "transform",
        }}
      />

      {/* darkening vignette as user scrolls */}
      <div
        ref={vignetteRef}
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)",
          opacity: 0.4,
          willChange: "opacity",
        }}
      />

      <div ref={contentRef} className="relative mx-auto w-full max-w-6xl" style={{ willChange: "transform, opacity" }}>
        <div
          ref={badgeRef}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[11px] uppercase tracking-[0.24em] text-white/85"
          style={{ willChange: "transform" }}
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 10px ${VOLT}` }}
          />
          Bytom · Tarnowskie Góry · Śląsk
        </div>

        <h1 className="max-w-5xl text-balance text-5xl font-light leading-[0.92] tracking-tighter text-white sm:text-7xl md:text-8xl">
          <span ref={line1Ref} className="inline-block" style={{ willChange: "transform" }}>
            Prąd, który <span style={{ color: VOLT }}>działa</span>.
          </span>
          <br />
          <span ref={line2Ref} className="inline-block text-white/60" style={{ willChange: "transform" }}>
            Bez kompromisów.
          </span>
        </h1>

        <p className="mt-8 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">
          20 lat na największych inwestycjach przemysłowych Śląska. Instalacje, automatyka, rozdzielnice.
        </p>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
          <a
            href="#realizacje"
            className="group inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-black transition"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 40px ${VOLT}55` }}
          >
            Zobacz realizacje
            <ArrowUpRight size={14} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#kontakt"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:border-white/40 hover:bg-white/[0.03]"
          >
            Szybki kontakt
          </a>
        </div>

        <div className="mt-20 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/[0.08] pt-8 md:gap-12">
          {[
            { v: "20+", l: "lat praktyki" },
            { v: "2500A", l: "rozdzielnice n/n" },
            { v: "PL · DE", l: "obszar realizacji" },
          ].map((s) => (
            <div key={s.l}>
              <div className="text-2xl font-light tracking-tight text-white md:text-3xl">{s.v}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/65">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* iOS tilt permission pill — only shows when needed */}
      {needsTiltPermission && !tiltReady && (
        <button
          type="button"
          onClick={() => (window as any).__elektrykEnableTilt?.()}
          className="absolute bottom-6 right-5 z-10 rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] backdrop-blur-sm"
          style={{ borderColor: `${VOLT}55`, color: VOLT, backgroundColor: "rgba(0,0,0,0.4)" }}
        >
          Włącz efekt 3D
        </button>
      )}
    </section>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-white/60">
      <span className="h-1 w-1 rounded-full" style={{ backgroundColor: VOLT, boxShadow: `0 0 6px ${VOLT}` }} />
      {children}
    </div>
  );
}

function Brand() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);
  const d2Ref = useRef<HTMLSpanElement | null>(null);
  const d0Ref = useRef<HTMLSpanElement | null>(null);
  const plusRef = useRef<HTMLSpanElement | null>(null);
  const captionRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const paraRef = useRef<HTMLParagraphElement | null>(null);
  const cardsRef = useRef<HTMLDivElement | null>(null);
  const ringRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const sec = sectionRef.current;
      if (!sec) return;
      const rect = sec.getBoundingClientRect();
      const vh = window.innerHeight;
      // p: 0 when section's top hits viewport bottom, 1 when section's bottom hits viewport top
      const p = clamp((vh - rect.top) / (vh + rect.height));

      if (labelRef.current) {
        labelRef.current.style.transform = `translate3d(${(1 - clamp(p * 3)) * -40}px, 0, 0)`;
        labelRef.current.style.opacity = String(clamp(p * 4));
      }
      if (d2Ref.current) {
        d2Ref.current.style.transform = `translate3d(${(p - 0.5) * -22}px, ${(p - 0.5) * -36}px, 0)`;
      }
      if (d0Ref.current) {
        d0Ref.current.style.transform = `translate3d(${(p - 0.5) * 14}px, ${(p - 0.5) * -18}px, 0)`;
      }
      if (plusRef.current) {
        const wobble = Math.sin(p * Math.PI) * 12;
        plusRef.current.style.transform = `translate3d(${(p - 0.5) * 30}px, ${wobble - 8}px, 0) scale(${1 + Math.sin(p * Math.PI) * 0.08})`;
      }
      if (captionRef.current) {
        captionRef.current.style.transform = `translate3d(0, ${(0.5 - p) * 40}px, 0)`;
      }
      if (headingRef.current) {
        headingRef.current.style.transform = `translate3d(0, ${(0.5 - p) * 60}px, 0)`;
      }
      if (paraRef.current) {
        paraRef.current.style.transform = `translate3d(0, ${(0.5 - p) * 36}px, 0)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(0, ${(p - 0.5) * -60}px, 0) rotate(${p * 30}deg)`;
        ringRef.current.style.opacity = String(clamp(p * 2 - 0.2));
      }
      if (cardsRef.current) {
        const cards = cardsRef.current.querySelectorAll<HTMLElement>("[data-pcard]");
        cards.forEach((el, i) => {
          const stagger = i * 0.05;
          const local = clamp((p - 0.2 - stagger) * 3);
          el.style.transform = `translate3d(0, ${(1 - local) * 60}px, 0)`;
          el.style.opacity = String(local);
        });
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="o-nas"
      className="relative overflow-hidden px-5 py-28 md:px-10 md:py-40"
      style={{ perspective: "1400px" }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      {/* Orbital ring behind "20+" */}
      <div
        ref={ringRef}
        aria-hidden
        className="pointer-events-none absolute left-[6%] top-[18%] h-[420px] w-[420px] rounded-full border md:left-[10%]"
        style={{
          borderColor: `${VOLT}22`,
          boxShadow: `inset 0 0 80px ${VOLT}10, 0 0 40px ${VOLT}10`,
          willChange: "transform, opacity",
        }}
      />

      <div className="relative mx-auto grid max-w-6xl gap-16 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <div ref={labelRef} style={{ willChange: "transform, opacity" }}>
            <SectionLabel>Rodowód</SectionLabel>
          </div>
          <div
            className="mt-10 flex items-baseline text-[140px] font-extralight leading-[0.85] tracking-tighter text-white md:text-[200px]"
            style={{ textShadow: `0 0 80px ${VOLT}22` }}
          >
            <span ref={d2Ref} className="inline-block" style={{ willChange: "transform" }}>
              2
            </span>
            <span ref={d0Ref} className="inline-block" style={{ willChange: "transform" }}>
              0
            </span>
            <span
              ref={plusRef}
              className="inline-block"
              style={{ color: VOLT, willChange: "transform", textShadow: `0 0 60px ${VOLT}` }}
            >
              +
            </span>
          </div>
          <div
            ref={captionRef}
            className="mt-3 text-xs uppercase tracking-[0.22em] text-white/70"
            style={{ willChange: "transform" }}
          >
            lat praktyki elektromontera
          </div>
        </div>

        <div className="md:col-span-7">
          <h2
            ref={headingRef}
            className="text-3xl font-light leading-tight tracking-tight text-white md:text-5xl"
            style={{ willChange: "transform" }}
          >
            Dwie dekady ciężkich inwestycji. <span className="text-white/70">Bez ściemy.</span>
          </h2>
          <p
            ref={paraRef}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/75"
            style={{ willChange: "transform" }}
          >
            Stawiamy na rzetelność techniczną, bezpieczną rezystancję i jakość wykonania.
          </p>

          <div ref={cardsRef} className="mt-12 grid gap-3 sm:grid-cols-2">
            {REALIZATIONS.map((r, i) => (
              <div
                key={r.client}
                data-pcard
                className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5 transition hover:border-[#00F0FF]/30 hover:bg-white/[0.03]"
                style={{ willChange: "transform, opacity", opacity: 0 }}
              >
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: VOLT }}>
                  <span>0{i + 1}</span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <div className="mt-3 text-sm font-medium text-white">{r.client}</div>
                <div className="mt-1.5 text-xs leading-relaxed text-white/50">{r.scope}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Qualifications() {
  return (
    <section id="uprawnienia" className="relative overflow-hidden bg-[#0E0E10] px-5 py-28 md:px-10 md:py-40 [content-visibility:auto] [contain-intrinsic-size:1px_900px]">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[400px]"
        style={{
          background: `radial-gradient(800px 400px at 50% 0%, ${VOLT}14, transparent 70%)`,
        }}
      />
      <div className="relative mx-auto max-w-6xl">
        <div className="flex flex-col gap-4">
          <SectionLabel>Uprawnienia</SectionLabel>
          <h2 className="max-w-3xl text-3xl font-light tracking-tighter text-white md:text-5xl">
            Certyfikaty <span className="text-white/70">i pełen zakres SEP.</span>
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-3 md:grid-cols-3 md:grid-rows-2 md:gap-4">
          <div className="group relative overflow-hidden rounded-3xl border border-white/[0.08] bg-gradient-to-br from-[#15151A] to-[#0B0B0D] p-7 md:col-span-2 md:row-span-2 md:p-10">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background: `radial-gradient(260px circle at 100% 0%, ${VOLT}1F, transparent 70%)`,
              }}
            />
            <div className="relative">
              <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.24em] text-white/70">
                <span className="h-px w-8" style={{ backgroundColor: VOLT }} />
                SEP — Eksploatacja + Dozór
              </div>
              <h3 className="mt-6 text-3xl font-light leading-tight tracking-tight text-white md:text-5xl">
                Pełny zakres uprawnień energetycznych.
              </h3>
              <ul className="mt-10 grid gap-4 sm:grid-cols-2">
                {SEP_ITEMS.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-white/75">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: VOLT }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {PARTNER_CERTS.map((c) => (
            <div
              key={c.brand}
              className="group relative overflow-hidden rounded-3xl border border-white/[0.06] bg-[#101013] p-6 transition hover:border-[#00F0FF]/30 md:p-7"
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-0 transition group-hover:opacity-100"
                style={{
                  background: `radial-gradient(220px circle at 20% 0%, ${VOLT}14, transparent 60%)`,
                }}
              />
              <div className="relative">
                <div className="text-[11px] font-bold uppercase tracking-[0.28em]" style={{ color: VOLT }}>
                  {c.brand}
                </div>
                <h4 className="mt-5 text-base font-medium leading-snug text-white">{c.title}</h4>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="uslugi" className="relative px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel>Usługi</SectionLabel>
            <h2 className="mt-5 max-w-2xl text-3xl font-light tracking-tighter text-white md:text-5xl">
              Sześć obszarów. <span className="text-white/70">Jeden standard.</span>
            </h2>
          </div>
          <a href="#kontakt" className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white">
            Zapytaj o wycenę →
          </a>
        </div>

        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-white/[0.06] bg-white/[0.04] sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.no}
                className="group relative bg-[#0A0A0C] p-8 transition-colors hover:bg-[#0E0E12] md:p-10"
              >
                <div className="flex items-start justify-between">
                  <span className="text-xs font-mono tracking-[0.2em] text-white/30">{s.no}</span>
                  <Icon size={20} className="text-white/25 transition group-hover:text-[#00F0FF]" />
                </div>
                <h3 className="mt-10 text-xl font-medium tracking-tight text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/50">{s.desc}</p>
                <div
                  className="mt-8 h-px w-8 transition-all duration-500 group-hover:w-24"
                  style={{ backgroundColor: VOLT, boxShadow: `0 0 10px ${VOLT}` }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section id="realizacje" className="relative overflow-hidden bg-[#08080A] px-5 py-28 md:px-10 md:py-40 [content-visibility:auto] [contain-intrinsic-size:1px_1200px]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF]/25 to-transparent" />
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <SectionLabel>Realizacje</SectionLabel>
            <h2 className="mt-5 max-w-2xl text-3xl font-light tracking-tighter text-white md:text-6xl">
              Wybrane prace <span className="text-white/70">z ostatniego roku.</span>
            </h2>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/75 md:text-base">
              Kilka świeżych projektów na rozgrzewkę. Pełne portfolio z filtrami kategorii czeka na osobnej stronie.
            </p>
          </div>
          <Link
            to="/demo/elektryk/realizacje"
            className="group inline-flex items-center gap-2 self-start rounded-full px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-black transition md:self-end"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 32px ${VOLT}55` }}
          >
            Zobacz wszystkie
            <ArrowUpRight size={14} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        {/* Featured grid (preview only — full gallery is on /demo/elektryk/realizacje) */}
        <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 md:[grid-auto-rows:200px] lg:[grid-auto-rows:240px]">
          {FEATURED.map((g, i) => {
              const span =
                g.span === "tall"
                  ? "md:row-span-2"
                  : g.span === "wide"
                    ? "md:col-span-2"
                    : "";
              return (
                <Link
                  key={g.title}
                  to="/demo/elektryk/realizacje"
                  className={`group relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#101013] text-left transition-colors hover:border-[#00F0FF]/40 ${span}`}
                  style={{ minHeight: 180 }}
                >
                  <img
                    src={g.img}
                    alt={g.title}
                    loading={i < 2 ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={i < 2 ? "high" : "low"}
                    width={720}
                    height={540}
                    className="absolute inset-0 h-full w-full object-cover opacity-80 transition-[transform,opacity] duration-500 group-hover:scale-105 group-hover:opacity-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute left-4 right-4 top-4 flex items-center justify-between">
                    <span className="rounded-full border border-white/15 bg-black/60 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-white/80">
                      {g.category}
                    </span>
                    <span
                      className="grid h-7 w-7 place-items-center rounded-full bg-white/15 text-white opacity-0 transition group-hover:opacity-100"
                      style={{ boxShadow: `0 0 18px ${VOLT}55` }}
                    >
                      <ArrowUpRight size={13} />
                    </span>
                  </div>

                  <div className="absolute inset-x-4 bottom-4">
                    <div className="text-sm font-medium leading-snug text-white md:text-base">{g.title}</div>
                    <div className="mt-1.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-white/75">
                      <MapPin size={10} />
                      {g.location}
                      <span className="opacity-40">·</span>
                      <Calendar size={10} />
                      {g.date}
                    </div>
                  </div>
                </Link>
              );
            })}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const listFn = useServerFn(elektrykPublicListReviews);
  const { data, isLoading } = useQuery({
    queryKey: ["elektryk", "reviews"],
    queryFn: () => listFn(),
    staleTime: 1000 * 60 * 10,
  });

  const allReviews = (data ?? []) as Array<{
    id: string;
    author: string;
    author_role: string | null;
    rating: number;
    content: string;
    source: string | null;
  }>;
  const reviews = allReviews.slice(0, 3);

  const avg =
    reviews.length > 0
      ? (reviews.reduce((s, r) => s + (r.rating ?? 0), 0) / reviews.length).toFixed(1)
      : "5.0";

  return (
    <section
      id="opinie"
      className="relative overflow-hidden bg-[#08080A] px-5 py-28 md:px-10 md:py-40 [content-visibility:auto] [contain-intrinsic-size:1px_900px]"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF]/25 to-transparent" />
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2"
        style={{ background: `radial-gradient(closest-side, ${VOLT}14, transparent 70%)` }}
      />
      <div className="relative mx-auto max-w-7xl">
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="min-w-0">
            <SectionLabel>Opinie klientów</SectionLabel>
            <h2 className="mt-5 max-w-2xl text-balance text-[28px] font-light leading-tight tracking-tighter text-white sm:text-4xl md:text-6xl">
              Co mówią o nas <span className="text-white/70">ci, którzy nam zaufali.</span>
            </h2>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/75 md:text-base">
              Opinie zebrane od inwestorów, firm produkcyjnych i wspólnot mieszkaniowych. Docelowo
              synchronizowane bezpośrednio z profilem Google.
            </p>
          </div>
          <div className="flex w-full shrink-0 items-center gap-4 rounded-2xl border border-white/[0.08] bg-white/[0.02] px-5 py-4 sm:w-auto sm:gap-5 sm:px-6 sm:py-5">
            <div className="text-4xl font-light leading-none text-white sm:text-5xl">{avg}</div>
            <div>
              <div className="flex items-center gap-0.5" aria-label={`Ocena ${avg} / 5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} fill={VOLT} stroke="none" />
                ))}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-white/60">
                {allReviews.length || "—"} opinii · Google
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-4 md:mt-12 md:grid-cols-3">
          {isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-56 animate-pulse rounded-3xl border border-white/[0.05] bg-white/[0.02]"
              />
            ))}
          {!isLoading &&
            reviews.map((r) => (
              <article
                key={r.id}
                className="group relative flex min-w-0 flex-col rounded-3xl border border-white/[0.06] bg-[#101013] p-5 transition hover:border-[#00F0FF]/30 sm:p-7"
              >
                <Quote
                  size={20}
                  className="text-white/15 transition group-hover:text-[#00F0FF]/40"
                />
                <div className="mt-3 flex items-center gap-0.5" aria-label={`${r.rating}/5`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={12}
                      fill={i < r.rating ? VOLT : "transparent"}
                      stroke={i < r.rating ? VOLT : "rgba(255,255,255,0.25)"}
                    />
                  ))}
                </div>
                <p className="mt-4 break-words text-sm leading-relaxed text-white/80">"{r.content}"</p>
                <div className="mt-6 flex items-center justify-between gap-3 border-t border-white/[0.05] pt-5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-white">{r.author}</div>
                    {r.author_role && (
                      <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.16em] text-white/55">
                        {r.author_role}
                      </div>
                    )}
                  </div>
                  <span
                    className="shrink-0 rounded-full border border-white/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-white/60"
                    title="Źródło"
                  >
                    {r.source ?? "google"}
                  </span>
                </div>
              </article>
            ))}
          {!isLoading && reviews.length === 0 && (
            <div className="rounded-3xl border border-dashed border-white/10 p-10 text-center text-sm text-white/55 md:col-span-3">
              Brak opinii do wyświetlenia.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section
      id="kontakt"
      className="relative overflow-hidden bg-[#0E0E10] px-5 py-28 md:px-10 md:py-40 [content-visibility:auto] [contain-intrinsic-size:1px_1100px]"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
        style={{
          background: `radial-gradient(520px 420px at 100% 0%, ${VOLT}1A, transparent 70%)`,
        }}
      />
      <div className="relative mx-auto max-w-6xl">
        <SectionLabel>Kontakt</SectionLabel>
        <h2 className="mt-5 max-w-3xl text-3xl font-light tracking-tighter text-white md:text-6xl">
          Porozmawiajmy <span className="text-white/70">o Twoim zleceniu.</span>
        </h2>

        <div className="mt-16 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <div className="space-y-7">
              <Detail label="Firma" value="ELEKTROPROFI — Dariusz Dulski" />
              <Detail
                label="Adres"
                value="ul. Chełmońskiego 33/11, 41-902 Bytom"
                icon={<MapPin size={14} style={{ color: VOLT }} />}
              />
              <Detail
                label="Telefon"
                value="+48 608 096 616"
                href="tel:+48608096616"
                icon={<Phone size={14} style={{ color: VOLT }} />}
                big
              />
              <Detail
                label="Stacjonarny"
                value="+48 32 307 20 31"
                href="tel:+48323072031"
                icon={<Phone size={14} style={{ color: VOLT }} />}
              />
              <Detail
                label="E-mail"
                value="kontakt@elektroprofi.pl"
                href="mailto:kontakt@elektroprofi.pl"
                icon={<Mail size={14} style={{ color: VOLT }} />}
              />
            </div>

            <a
              href="https://wa.me/48608096616"
              target="_blank"
              rel="noreferrer"
              className="mt-12 inline-flex w-full items-center justify-center gap-3 rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-black transition hover:translate-y-[-1px] sm:w-auto"
              style={{ backgroundColor: VOLT, boxShadow: `0 0 40px ${VOLT}55` }}
            >
              <MessageCircle size={16} />
              Napisz na WhatsApp
            </a>
          </div>

          <form
            onSubmit={(e) => e.preventDefault()}
            className="space-y-8 rounded-3xl border border-white/[0.07] bg-[#0A0A0C] p-7 md:p-10"
          >
            <FormField label="Imię i nazwisko lub firma" name="name" />
            <FormField label="Numer telefonu" name="phone" type="tel" />
            <FormField label="Adres e-mail" name="email" type="email" />
            <FormField label="Treść wiadomości" name="msg" textarea />
            <button
              type="submit"
              className="group inline-flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-black transition hover:translate-y-[-1px] sm:w-auto"
              style={{ backgroundColor: VOLT, boxShadow: `0 0 30px ${VOLT}55` }}
            >
              Wyślij zapytanie
              <ArrowUpRight size={14} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Detail({
  label,
  value,
  href,
  icon,
  big,
}: {
  label: string;
  value: string;
  href?: string;
  icon?: React.ReactNode;
  big?: boolean;
}) {
  const body = (
    <div className={`mt-1.5 flex items-center gap-2 ${big ? "text-2xl md:text-3xl" : "text-base"} font-light tracking-tight text-white`}>
      {icon}
      {value}
    </div>
  );
  return (
    <div className="border-l border-white/10 pl-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">{label}</div>
      {href ? (
        <a href={href} className="block transition hover:text-[#00F0FF]">
          {body}
        </a>
      ) : (
        body
      )}
    </div>
  );
}

function FormField({
  label,
  name,
  type = "text",
  textarea = false,
}: {
  label: string;
  name: string;
  type?: string;
  textarea?: boolean;
}) {
  const base =
    "peer block w-full border-0 border-b border-white/15 bg-transparent px-0 py-3 text-sm text-white placeholder-transparent transition focus:border-[#00F0FF] focus:outline-none focus:ring-0";
  return (
    <div className="relative">
      {textarea ? (
        <textarea id={name} name={name} placeholder={label} rows={3} className={base} />
      ) : (
        <input id={name} name={name} type={type} placeholder={label} className={base} />
      )}
      <label
        htmlFor={name}
        className="pointer-events-none absolute left-0 -top-2 text-[10px] uppercase tracking-[0.22em] text-white/70 transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-sm peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-placeholder-shown:text-white/30 peer-focus:-top-2 peer-focus:text-[10px] peer-focus:uppercase peer-focus:tracking-[0.22em] peer-focus:text-[#00F0FF]"
      >
        {label}
      </label>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/[0.05] bg-[#070708] px-5 py-10 md:px-10">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 text-[11px] uppercase tracking-[0.22em] text-white/70 md:flex-row md:items-center">
        <div className="flex items-center">
          ELEKTROPROFI
          <span
            className="mx-2 inline-block h-1 w-1 rounded-full align-middle"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 6px ${VOLT}` }}
          />
          Dariusz Dulski · Bytom
        </div>
        <div>© {new Date().getFullYear()} · Wszelkie prawa zastrzeżone</div>
      </div>
    </footer>
  );
}
